import { useEffect, useRef, useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { useDispatch } from "react-redux";
import logo from "../../assets/Login Page/Desktop/Logo.png";
import {
  useLoginMutation,
  useVerifyLoginOtpMutation,
} from "../../redux-toolkit/features/auth/authApiSlice";
import { isErrorRedirect } from "../../redux-toolkit/features/auth/authSlice";
import { getNetworkAwareErrorMessage } from "./LoginForm";

const CODE_LENGTH = 6;
const RESEND_COOLDOWN_MS = 45 * 1000;

const formatCountdown = (ms) => {
  const totalSeconds = Math.max(0, Math.ceil(ms / 1000));
  const minutes = Math.floor(totalSeconds / 60);
  const seconds = totalSeconds % 60;
  return `${minutes}:${String(seconds).padStart(2, "0")}`;
};

export const OtpLogin = ({
  email,
  id,
  password,
  rememberFlag,
  onBack,
  onLoginSuccess,
}) => {
  const navigate = useNavigate();
  const dispatch = useDispatch();
  const [verifyLoginOtp] = useVerifyLoginOtpMutation();
  const [login] = useLoginMutation();
  const [digits, setDigits] = useState(() => Array(CODE_LENGTH).fill(""));
  const [inlineError, setInlineError] = useState("");
  const [resendNotice, setResendNotice] = useState("");
  const [cooldownRemainingMs, setCooldownRemainingMs] = useState(0);
  const [cooldownToken, setCooldownToken] = useState(0);
  const inputs = useRef([]);
  const submitting = useRef(false);
  const cooldownEndsAt = useRef(0);

  useEffect(() => {
    inputs.current[0]?.focus();
  }, []);

  useEffect(() => {
    if (!cooldownToken) return undefined;
    let timer;
    const tick = () => {
      const remaining = cooldownEndsAt.current - Date.now();
      setCooldownRemainingMs(Math.max(0, remaining));
      if (remaining <= 0 && timer) window.clearInterval(timer);
    };
    tick();
    timer = window.setInterval(tick, 250);
    return () => window.clearInterval(timer);
  }, [cooldownToken]);

  const startResendCooldown = () => {
    cooldownEndsAt.current = Date.now() + RESEND_COOLDOWN_MS;
    setCooldownRemainingMs(RESEND_COOLDOWN_MS);
    setCooldownToken((token) => token + 1);
  };

  const submitCode = async (value) => {
    if (submitting.current || value.length !== CODE_LENGTH) return;
    submitting.current = true;
    setInlineError("");
    setResendNotice("");
    dispatch(isErrorRedirect({ redirect: false }));
    try {
      await verifyLoginOtp({
        id,
        code: value,
        rememberFlag: rememberFlag || false,
      }).unwrap();
      navigate("/auth");
      onLoginSuccess && onLoginSuccess();
    } catch (err) {
      const message = getNetworkAwareErrorMessage(err);
      setInlineError(message);
      dispatch(isErrorRedirect({ redirect: true }));
      setDigits(Array(CODE_LENGTH).fill(""));
      inputs.current[0]?.focus();
      if (/enter your password again/i.test(message)) onBack();
    } finally {
      submitting.current = false;
    }
  };

  const updateDigits = (next) => {
    setInlineError("");
    setDigits(next);
    const code = next.join("");
    if (code.length === CODE_LENGTH && !next.includes("")) {
      submitCode(code);
    }
  };

  const handleChange = (index, raw) => {
    const chars = raw.replace(/\D/g, "");
    if (!chars) {
      const next = [...digits];
      next[index] = "";
      setInlineError("");
      setDigits(next);
      return;
    }
    const next = [...digits];
    chars.split("").forEach((char, offset) => {
      if (index + offset < CODE_LENGTH) next[index + offset] = char;
    });
    const focusAt = Math.min(index + chars.length, CODE_LENGTH - 1);
    inputs.current[focusAt]?.focus();
    updateDigits(next);
  };

  const handleKeyDown = (index, event) => {
    if (event.key === "Backspace" && !digits[index] && index > 0) {
      const next = [...digits];
      next[index - 1] = "";
      setInlineError("");
      setDigits(next);
      inputs.current[index - 1]?.focus();
    }
    if (event.key === "ArrowLeft" && index > 0) inputs.current[index - 1]?.focus();
    if (event.key === "ArrowRight" && index < CODE_LENGTH - 1) {
      inputs.current[index + 1]?.focus();
    }
  };

  const handlePaste = (event) => {
    const pasted = event.clipboardData.getData("text").replace(/\D/g, "").slice(0, CODE_LENGTH);
    if (!pasted) return;
    event.preventDefault();
    const next = Array(CODE_LENGTH).fill("");
    pasted.split("").forEach((char, index) => {
      next[index] = char;
    });
    inputs.current[Math.min(pasted.length, CODE_LENGTH - 1)]?.focus();
    updateDigits(next);
  };

  const resend = async () => {
    if (submitting.current || cooldownRemainingMs > 0) return;
    submitting.current = true;
    setInlineError("");
    try {
      await login({ id, password, rememberFlag: rememberFlag || false }).unwrap();
      setDigits(Array(CODE_LENGTH).fill(""));
      setResendNotice(`A new code was sent to ${email}.`);
      startResendCooldown();
      inputs.current[0]?.focus();
    } catch (err) {
      const message = getNetworkAwareErrorMessage(err);
      setInlineError(message);
      dispatch(isErrorRedirect({ redirect: true }));
      if (
        /enter your password again|password sign-in is locked|sign-in codes are locked/i.test(
          message,
        )
      ) {
        onBack();
      }
    } finally {
      submitting.current = false;
    }
  };

  const resendDisabled = cooldownRemainingMs > 0;

  return (
    <div className="otpLogin">
      <div className="otpLogin__panel">
        <img src={logo} className="otpLogin__logo" alt="The Quest" />
        <div className="otpLogin__body">
          <div className="otpLogin__heading">
            <h1 className="otpLogin__title">Check your email</h1>
            <p className="otpLogin__copy">
              If you have an account on The Quest, we sent a code to {email}.
            </p>
          </div>
          <div className="otpLogin__boxes" onPaste={handlePaste}>
            {digits.map((digit, index) => (
              <input
                key={index}
                ref={(node) => {
                  inputs.current[index] = node;
                }}
                className="otpLogin__box"
                type="text"
                inputMode="numeric"
                autoComplete={index === 0 ? "one-time-code" : "off"}
                maxLength={index === 0 ? CODE_LENGTH : 1}
                aria-label={`Digit ${index + 1}`}
                value={digit}
                onChange={(event) => handleChange(index, event.target.value)}
                onKeyDown={(event) => handleKeyDown(index, event)}
              />
            ))}
          </div>
          {inlineError ? (
            <p className="otpLogin__error" role="alert">
              {inlineError}
            </p>
          ) : null}
          {resendNotice ? (
            <p className="otpLogin__notice" role="status">
              {resendNotice}
            </p>
          ) : null}
          <div className="otpLogin__actions">
            <button type="button" className="otpLogin__link" onClick={onBack}>
              Use a Different Email
            </button>
            <p className="otpLogin__resend">
              Didn&apos;t receive the code?{" "}
              <button
                type="button"
                className="otpLogin__resendBtn"
                onClick={resend}
                disabled={resendDisabled}
              >
                {resendDisabled
                  ? `Resend in ${formatCountdown(cooldownRemainingMs)}`
                  : "Resend"}
              </button>
            </p>
          </div>
        </div>
      </div>
      <div className="otpLogin__footer">
        <p className="otpLogin__legal">
          <Link to="/privacy-policy">Privacy&nbsp;Policy</Link>
          {" & "}
          <Link to="/terms-and-conditions">Terms&nbsp;of&nbsp;Service</Link>
        </p>
        <p className="login__powered">
          Powered by &nbsp;
          <a href="https://www.sliqgroup.com" rel="noreferrer" target="_blank">
            <b>Sliq Group</b>
          </a>
        </p>
      </div>
    </div>
  );
};
